// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function ProcessSection() {
  const { t } = useTranslation('servicesTestEquipmentModernization')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const steps = [
    {
      n: '01',
      title: t('process.steps.1.title'),
      text: t('process.steps.1.description')
    },
    {
      n: '02',
      title: t('process.steps.2.title'),
      text: t('process.steps.2.description')
    },
    {
      n: '03',
      title: t('process.steps.3.title'),
      text: t('process.steps.3.description')
    },
    {
      n: '04',
      title: t('process.steps.4.title'),
      text: t('process.steps.4.description')
    },
    {
      n: '05',
      title: t('process.steps.5.title'),
      text: t('process.steps.5.description')
    }
  ]

  /**
   * A vertical timeline: the five steps are sequential, so they read better
   * stacked with a connecting rail than as a row of equal cards.
   */
  const getStep = (
    step: { n: string; title: string; text: string },
    isLast: boolean
  ) => (
    <Box key={step.n} sx={{ display: 'flex', gap: '1.5rem' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            width: '3rem',
            height: '3rem',
            borderRadius: '50%',
            background: themeColors.kroemtech.background,
            border: `2px solid ${themeColors.success.main}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: themeColors.success.darker
            }}
          >
            {step.n}
          </Typography>
        </Box>

        {!isLast && (
          <Box
            sx={{
              flex: 1,
              width: '2px',
              minHeight: '1.5rem',
              background: themeColors.gray.lighter
            }}
          />
        )}
      </Box>

      <Box sx={{ paddingBottom: isLast ? 0 : '2rem', maxWidth: '46rem' }}>
        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontSize: '1.05rem',
            fontWeight: 700,
            lineHeight: 1.3,
            color: themeColors.kroemtech.dunkelblau,
            marginTop: '0.6rem',
            marginBottom: '0.6rem'
          }}
        >
          {step.title}
        </Typography>
        <Typography
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.7,
            color: themeColors.gray.defaultText
          }}
        >
          {step.text}
        </Typography>
      </Box>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('process.eyebrow')}
        title={t('process.title')}
      />

      <Box sx={{ paddingLeft: stacked ? 0 : '0.5rem' }}>
        {steps.map((step, i) => getStep(step, i === steps.length - 1))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
